import { onMounted, ref, watch, type Ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import type {
  FinalAudioOptionsResponse,
  FinalAudioWorkspaceGenerateRequest,
  FinalAudioWorkspaceGenerateResponse,
  FinalAudioWorkspaceMixRequest,
  FinalAudioWorkspaceMixResponse,
} from '@/types/workflow'
import type { WorkflowKnowledgeAudioSelectionItem } from '@/utils/workflowKnowledge'

import {
  buildNarrationSegmentMixRequest,
  clampTimelineStartMs,
  createNarrationTimelineSegment,
  findNarrationTimelineConflict,
  formatTimelineTime,
  sortNarrationTimelineSegments,
} from './finalAudioTimeline'
import type {
  AudioMixControlDraft,
  FinalAudioNarrationSegmentDraft,
  FinalVideoPlaybackSource,
  StoryboardWorkspaceState,
  UploadedWorkspaceAudio,
  UploadedWorkspaceVideo,
} from './types'

interface UploadedMaterial {
  id?: number
  name?: string
  file_url?: string
  updated_at?: string
}

interface UseFinalAudioStudioOptions {
  projectId: number
  bgmUploadInput: Ref<HTMLInputElement | null>
  finalTtsScript: Ref<string>
  finalTtsStartMs: Ref<number>
  finalTtsVoiceId: Ref<string>
  finalTtsProvider: Ref<string>
  finalTtsSpeed: Ref<number>
  finalTtsTimelineSegments: Ref<FinalAudioNarrationSegmentDraft[]>
  uploadedFinalBgmAudio: Ref<UploadedWorkspaceAudio | null>
  audioMixControls: Ref<AudioMixControlDraft>
  audioMixPreviewVideo: Ref<UploadedWorkspaceVideo | null>
  audioMixDeliveryVideo: Ref<UploadedWorkspaceVideo | null>
  preferredFinalVideoSource: Ref<FinalVideoPlaybackSource>
  routeFullPath: () => string
  routerPush: (location: RouteLocationRaw) => Promise<unknown>
  consumeQueuedKnowledgeAudioSelections: (projectId: number) => WorkflowKnowledgeAudioSelectionItem[]
  getStoryboardWorkspaceStatePayload: () => StoryboardWorkspaceState
  persistStoryboardWorkspaceState: (nextState: StoryboardWorkspaceState, options?: { successMessage?: string; silent?: boolean }) => Promise<void>
  uploadMaterial: (formData: FormData, options: { project_id: number; material_type: string; description: string }) => Promise<UploadedMaterial>
  getFinalAudioOptionsApi: (workflowId: number, params?: { provider?: string }) => Promise<FinalAudioOptionsResponse>
  generateFinalAudioTtsApi: (workflowId: number, params: FinalAudioWorkspaceGenerateRequest) => Promise<FinalAudioWorkspaceGenerateResponse>
  mixFinalAudioIntoVideoApi: (workflowId: number, params: FinalAudioWorkspaceMixRequest) => Promise<FinalAudioWorkspaceMixResponse>
  success: (message: string) => void
  error: (message: string) => void
}

export function useFinalAudioStudio(options: UseFinalAudioStudioOptions) {
  const audioOptionsLoading = ref(false)
  const ttsGenerating = ref(false)
  const bgmUploading = ref(false)
  const finalAudioMixing = ref(false)
  const ttsVoiceOptions = ref<Array<{ label: string; value: string; description?: string | null }>>([])

  async function loadAudioOptions(providerOverride = options.finalTtsProvider.value || undefined) {
    audioOptionsLoading.value = true
    try {
      const response = await options.getFinalAudioOptionsApi(
        options.projectId,
        providerOverride ? { provider: providerOverride } : undefined,
      )
      options.finalTtsProvider.value = String(response.provider || providerOverride || '').trim()
      ttsVoiceOptions.value = Array.isArray(response.voices) ? response.voices : []

      if (!ttsVoiceOptions.value.some((item) => item.value === options.finalTtsVoiceId.value)) {
        options.finalTtsVoiceId.value = String(response.default_voice_id || '').trim()
      }
      if (!Number.isFinite(options.finalTtsSpeed.value) || options.finalTtsSpeed.value <= 0) {
        options.finalTtsSpeed.value = Number(response.speed?.default ?? 1) || 1
      }
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.message || '加载音色配置失败')
    } finally {
      audioOptionsLoading.value = false
    }
  }

  async function persistFinalAudioState(successMessage?: string, silent = true) {
    const current = options.getStoryboardWorkspaceStatePayload()
    await options.persistStoryboardWorkspaceState(current, { successMessage, silent: silent && !successMessage })
  }

  function resetMixPreview() {
    options.audioMixPreviewVideo.value = null
  }

  function describeTimelineConflict(segments: FinalAudioNarrationSegmentDraft[]) {
    const conflict = findNarrationTimelineConflict(segments)
    if (!conflict) {
      return ''
    }

    const previousLabel = conflict.previous.script || conflict.previous.audio?.name || '上一段 TTS'
    const currentLabel = conflict.current.script || conflict.current.audio?.name || '当前 TTS'
    return `${currentLabel} 与 ${previousLabel} 在 ${formatTimelineTime(conflict.current.start_ms)} 附近发生重叠，请保证同一时间只有一段 TTS 播放。`
  }

  function commitNarrationSegments(
    nextSegments: FinalAudioNarrationSegmentDraft[],
    optionsOverride: { successMessage?: string; silent?: boolean } = {},
  ) {
    const sortedSegments = sortNarrationTimelineSegments(nextSegments)
    const conflictMessage = describeTimelineConflict(sortedSegments)
    if (conflictMessage) {
      options.error(conflictMessage)
      return false
    }

    options.finalTtsTimelineSegments.value = sortedSegments
    resetMixPreview()
    void persistFinalAudioState(optionsOverride.successMessage, optionsOverride.silent ?? !optionsOverride.successMessage)
    return true
  }

  function appendNarrationSegment(segment: FinalAudioNarrationSegmentDraft, successMessage: string) {
    return commitNarrationSegments([...options.finalTtsTimelineSegments.value, segment], { successMessage, silent: false })
  }

  async function applyQueuedAudioSelections() {
    const queuedSelections = options.consumeQueuedKnowledgeAudioSelections(options.projectId)
    if (!queuedSelections.length) {
      return
    }

    let changed = false
    for (const item of queuedSelections) {
      if (!String(item.url || '').trim()) {
        continue
      }

      if (item.role === 'narration') {
        const appended = appendNarrationSegment(createNarrationTimelineSegment({
          script: options.finalTtsScript.value.trim() || String(item.name || '旁白片段').trim(),
          start_ms: options.finalTtsStartMs.value,
          audio: {
            url: String(item.url || '').trim(),
            name: String(item.name || '旁白素材').trim(),
            material_id: item.material_id,
            duration_seconds: item.duration_seconds ?? null,
          },
          provider: undefined,
          voice_id: undefined,
          speed: options.finalTtsSpeed.value,
        }), '已从素材中心加入 TTS 时间线')
        if (appended) {
          options.finalTtsScript.value = ''
          changed = true
        }
        continue
      }

      options.uploadedFinalBgmAudio.value = {
        url: String(item.url || '').trim(),
        name: String(item.name || 'BGM 素材').trim(),
        material_id: item.material_id,
      }
      changed = true
    }

    if (!changed) {
      return
    }

    if (queuedSelections.some((item) => item.role === 'bgm')) {
      resetMixPreview()
      await persistFinalAudioState(undefined, true)
      options.success('已从素材中心带回音频素材')
    }
  }

  function handleOpenAudioLibrary(role: 'narration' | 'bgm') {
    void options.routerPush({
      path: '/knowledge',
      query: {
        projectId: String(options.projectId),
        stage: 'final',
        mode: 'final-audio',
        audioRole: role,
        tab: 'audio',
        return: options.routeFullPath(),
      },
    })
  }

  async function handleGenerateTts() {
    const script = options.finalTtsScript.value.trim()
    if (!script) {
      options.error('请先输入要生成旁白的文本')
      return
    }

    ttsGenerating.value = true
    try {
      const response = await options.generateFinalAudioTtsApi(options.projectId, {
        script,
        provider: options.finalTtsProvider.value || undefined,
        voice_id: options.finalTtsVoiceId.value || undefined,
        speed: options.finalTtsSpeed.value,
      })
      options.finalTtsProvider.value = String(response.provider || options.finalTtsProvider.value || '').trim()
      options.finalTtsVoiceId.value = String(response.voice_id || options.finalTtsVoiceId.value || '').trim()
      options.finalTtsSpeed.value = Number(response.speed ?? options.finalTtsSpeed.value ?? 1) || 1
      const segmentAdded = appendNarrationSegment(createNarrationTimelineSegment({
        script,
        start_ms: options.finalTtsStartMs.value,
        audio: {
          url: String(response.audio_url || '').trim(),
          name: String(response.material_name || 'AI 旁白音频').trim(),
          material_id: response.material_id ?? undefined,
          duration_seconds: response.duration_seconds ?? null,
          provider: options.finalTtsProvider.value,
          voice_id: options.finalTtsVoiceId.value,
        },
        provider: options.finalTtsProvider.value,
        voice_id: options.finalTtsVoiceId.value,
        speed: options.finalTtsSpeed.value,
      }), '旁白片段已加入时间线')
      if (segmentAdded) {
        options.finalTtsScript.value = ''
      }
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.message || '生成旁白音频失败')
    } finally {
      ttsGenerating.value = false
    }
  }

  function triggerBgmUpload() {
    if (!options.bgmUploadInput.value) {
      return
    }
    options.bgmUploadInput.value.value = ''
    options.bgmUploadInput.value.click()
  }

  async function handleBgmFileSelected(event: Event) {
    const target = event.target as HTMLInputElement | null
    const file = target?.files?.[0]
    if (!file) {
      return
    }

    bgmUploading.value = true
    try {
      const formData = new FormData()
      formData.append('file', file)
      const material = await options.uploadMaterial(formData, {
        project_id: options.projectId,
        material_type: 'music_file',
        description: 'final-delivery-bgm',
      })
      options.uploadedFinalBgmAudio.value = {
        url: String(material.file_url || '').trim(),
        name: String(material.name || file.name || 'BGM').trim(),
        material_id: material.id,
        uploaded_at: material.updated_at,
      }
      options.audioMixPreviewVideo.value = null
      await persistFinalAudioState('BGM 已上传', false)
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.message || '上传 BGM 失败')
    } finally {
      bgmUploading.value = false
      if (target) {
        target.value = ''
      }
    }
  }

  async function handleGenerateMixPreview() {
    const narrationSegments = buildNarrationSegmentMixRequest(options.finalTtsTimelineSegments.value)
    const bgmUrl = String(options.uploadedFinalBgmAudio.value?.url || '').trim()
    if (!narrationSegments.length && !bgmUrl) {
      options.error('请先生成旁白或上传 BGM')
      return
    }

    const conflictMessage = describeTimelineConflict(options.finalTtsTimelineSegments.value)
    if (conflictMessage) {
      options.error(conflictMessage)
      return
    }

    finalAudioMixing.value = true
    try {
      const response = await options.mixFinalAudioIntoVideoApi(options.projectId, {
        narration_segments: narrationSegments,
        bgm_url: bgmUrl || undefined,
        keep_original_audio: options.audioMixControls.value.keep_original_audio,
        narration_volume: options.audioMixControls.value.narration_volume,
        bgm_volume: options.audioMixControls.value.bgm_volume,
        original_audio_volume: options.audioMixControls.value.original_audio_volume,
        ducking_enabled: options.audioMixControls.value.ducking_enabled,
      })
      options.audioMixPreviewVideo.value = {
        url: String(response.video_url || '').trim(),
        name: String(response.material_name || '混音预览版').trim(),
        material_id: response.material_id ?? undefined,
      }
      await persistFinalAudioState('混音预览已生成', false)
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.message || '生成混音预览失败')
    } finally {
      finalAudioMixing.value = false
    }
  }

  async function handleApplyPreviewAsDelivery() {
    if (!options.audioMixPreviewVideo.value?.url) {
      options.error('请先生成混音预览')
      return
    }

    options.audioMixDeliveryVideo.value = { ...options.audioMixPreviewVideo.value }
    options.preferredFinalVideoSource.value = 'audio_mix_delivery'
    await persistFinalAudioState('已设为当前交付版', false)
  }

  async function clearGeneratedTts() {
    options.finalTtsTimelineSegments.value = []
    resetMixPreview()
    await persistFinalAudioState(undefined, true)
  }

  async function clearUploadedBgm() {
    options.uploadedFinalBgmAudio.value = null
    resetMixPreview()
    await persistFinalAudioState(undefined, true)
  }

  async function handleRemoveTtsSegment(segmentId: string) {
    const nextSegments = options.finalTtsTimelineSegments.value.filter((item) => item.id !== segmentId)
    options.finalTtsTimelineSegments.value = nextSegments
    resetMixPreview()
    await persistFinalAudioState(undefined, true)
  }

  async function handleUpdateTtsSegmentStartMs(segmentId: string, startMs: number) {
    const nextSegments = options.finalTtsTimelineSegments.value.map((segment) => {
      if (segment.id !== segmentId) {
        return segment
      }

      return {
        ...segment,
        start_ms: clampTimelineStartMs(startMs),
      }
    })

    const conflictMessage = describeTimelineConflict(nextSegments)
    if (conflictMessage) {
      options.error(conflictMessage)
      return false
    }

    options.finalTtsTimelineSegments.value = sortNarrationTimelineSegments(nextSegments)
    resetMixPreview()
    await persistFinalAudioState(undefined, true)
    return true
  }

  watch(
    () => options.finalTtsProvider.value,
    (nextProvider, previousProvider) => {
      if (!nextProvider || nextProvider === previousProvider || audioOptionsLoading.value) {
        return
      }

      options.finalTtsVoiceId.value = ''
      void loadAudioOptions(nextProvider)
    },
  )

  onMounted(() => {
    void loadAudioOptions()
    void applyQueuedAudioSelections()
  })

  return {
    audioOptionsLoading,
    bgmUploading,
    clearGeneratedTts,
    clearUploadedBgm,
    finalAudioMixing,
    handleApplyPreviewAsDelivery,
    handleBgmFileSelected,
    handleGenerateMixPreview,
    handleGenerateTts,
    handleOpenAudioLibrary,
    handleRemoveTtsSegment,
    handleUpdateTtsSegmentStartMs,
    loadAudioOptions,
    triggerBgmUpload,
    ttsGenerating,
    ttsVoiceOptions,
  }
}